import type { ServiceCategoryConfig } from 'config-types/config/booking';
import type { FormFieldSection, QuestionnaireConfigType } from 'config-types/config/form-fields';
import { HomepageOptions } from 'config-types/config/homepage-options';
import type { FormFieldTrigger, QuestionnaireBase } from 'config-types/config/questionnaire';
import { Coding, Questionnaire, Slot } from 'fhir/r4b';
import z from 'zod';
import {
  CONFIG_INJECTION_KEYS,
  createProxyConfigObject,
  mergeAndFreezeConfigObjects,
} from '../../config-helpers/helpers';
import {
  buildReasonForVisitFromConfig,
  createQuestionnaireFromConfig,
} from '../../config-helpers/shared-questionnaire';
import { SERVICE_CATEGORY_SYSTEM } from '../../fhir/constants';
import { CanonicalUrl } from '../../types/common';
import { BRANDING_CONFIG } from '../branding';
import { VALUE_SETS } from '../value-sets';

// --- Data inlined from defaults.ts ---

export interface StrongCoding extends Coding {
  code: string;
  display: string;
  system: string;
}

export interface BookingOption {
  id: string;
  label: string;
}

const PatientDoesntExistTriggerEnableAndRequire: FormFieldTrigger = {
  targetQuestionLinkId: 'existing-patient-id',
  effect: ['enable', 'require'],
  operator: 'exists',
  answerBoolean: false,
};

const PatientDoesntExistTriggerEnableOnly: FormFieldTrigger = {
  targetQuestionLinkId: 'existing-patient-id',
  effect: ['enable'],
  operator: 'exists',
  answerBoolean: false,
};

const getFormFields = (
  serviceCategories: ServiceCategoryConfig[] = SERVICE_CATEGORIES_AVAILABLE
): Record<string, FormFieldSection> => {
  const hiddenFields: string[] = [
    'patient-preferred-name',
    'patient-weight',
    'patient-ssn',
    'authorized-non-legal-guardian',
  ];
  const requiredFields = ['patient-birth-sex', 'patient-email'];
  const { reasonForVisit, isHidden } = buildReasonForVisitFromConfig(serviceCategories);
  if (isHidden) {
    hiddenFields.push(reasonForVisit.key);
  } else {
    requiredFields.push(reasonForVisit.key);
  }
  return {
    patientInfo: {
      linkId: 'patient-information-page',
      title: 'About the patient',
      logicalItems: {
        shouldDisplaySsnField: {
          key: 'should-display-ssn-field',
          type: 'boolean',
          initialValue: false,
        },
        ssnFieldRequired: {
          key: 'ssn-field-required',
          type: 'boolean',
        },
        existingPatientId: {
          key: 'existing-patient-id',
          type: 'string',
        },
        appointmentServiceCategory: {
          key: 'appointment-service-category',
          type: 'string',
        },
        appointmentServiceMode: {
          key: 'appointment-service-mode',
          type: 'string',
        },
      },
      items: {
        firstName: {
          key: 'patient-first-name',
          label: 'First name (legal)',
          type: 'string',
          disabledDisplay: 'hidden',
          triggers: [PatientDoesntExistTriggerEnableAndRequire],
        },
        middleName: {
          key: 'patient-middle-name',
          label: 'Middle name (legal)',
          type: 'string',
          disabledDisplay: 'hidden',
          triggers: [PatientDoesntExistTriggerEnableOnly],
        },
        lastName: {
          key: 'patient-last-name',
          label: 'Last name (legal)',
          type: 'string',
          disabledDisplay: 'hidden',
          triggers: [PatientDoesntExistTriggerEnableAndRequire],
        },
        preferredName: {
          key: 'patient-preferred-name',
          label: 'Chosen or preferred name (optional)',
          type: 'string',
        },
        dateOfBirth: {
          key: 'patient-birthdate',
          label: 'Date of birth',
          type: 'date',
          dataType: 'DOB',
          triggers: [PatientDoesntExistTriggerEnableAndRequire],
        },
        birthSex: {
          key: 'patient-birth-sex',
          label: 'Birth sex',
          type: 'choice',
          options: VALUE_SETS.birthSexOptions,
        },
        weight: {
          key: 'patient-weight',
          label: 'Weight (lbs)',
          type: 'decimal',
          triggers: [
            {
              targetQuestionLinkId: 'appointment-service-mode',
              effect: ['enable'],
              operator: '=',
              answerString: 'virtual',
            },
          ],
          disabledDisplay: 'hidden',
        },
        ssn: {
          key: 'patient-ssn',
          label: 'SSN',
          type: 'string',
          dataType: 'SSN',
          disabledDisplay: 'hidden',
          triggers: [
            {
              targetQuestionLinkId: 'should-display-ssn-field',
              effect: ['enable'],
              operator: '=',
              answerBoolean: true,
            },
            {
              targetQuestionLinkId: 'ssn-field-required',
              effect: ['require'],
              operator: '=',
              answerBoolean: true,
            },
          ],
        },
        email: {
          key: 'patient-email',
          label: 'Email',
          type: 'string',
          dataType: 'Email',
          triggers: [
            {
              targetQuestionLinkId: 'patient-no-email',
              effect: ['enable'],
              operator: '!=',
              answerBoolean: true,
            },
            {
              targetQuestionLinkId: 'patient-no-email',
              effect: ['filter'],
              operator: '=',
              answerBoolean: true,
            },
          ],
          disabledDisplay: 'hidden',
        },
        noEmail: {
          key: 'patient-no-email',
          label: "Don't have email",
          type: 'boolean',
        },
        returnPatientCheck: {
          key: 'return-patient-check',
          label: `Have you been to ${BRANDING_CONFIG.projectName} in the past 3 years?`,
          type: 'choice',
          disabledDisplay: 'hidden',
          options: VALUE_SETS.yesNoOptions,
          triggers: [PatientDoesntExistTriggerEnableAndRequire],
        },
        // Single RFV field with display filters, auto-generated from service category config
        reasonForVisit,
        tellUsMore: {
          key: 'tell-us-more',
          label: 'Tell us more',
          type: 'string',
          triggers: [
            {
              targetQuestionLinkId: 'reason-for-visit',
              effect: ['enable', 'require'],
              operator: '=',
              answerString: 'Other',
            },
          ],
          enableBehavior: 'any',
          disabledDisplay: 'hidden',
        },
        authorizedNonLegalGuardians: {
          key: 'authorized-non-legal-guardian',
          label: 'Who, besides the parent or legal guardian, is allowed to bring in the patient?',
          type: 'string',
        },
      },
      hiddenFields,
      requiredFields,
    },
  };
};

const hiddenFormSections: string[] = [];

const questionnaireBaseDefaults: QuestionnaireBase = {
  resourceType: 'Questionnaire',
  url: 'https://ottehr.com/FHIR/Questionnaire/book-appointment',
  version: '1.0.1',
  name: 'BookAppointmentQuestionnaire',
  title: 'Book Appointment Form',
  status: 'active',
};

const getFormDefaults = (): QuestionnaireConfigType => ({
  questionnaireBase: questionnaireBaseDefaults,
  hiddenFormSections,
  FormFields: getFormFields(),
});

export const SERVICE_CATEGORIES_AVAILABLE: ServiceCategoryConfig[] = [
  {
    category: { display: 'Urgent Care', code: 'urgent-care', system: SERVICE_CATEGORY_SYSTEM },
    serviceModes: ['in-person', 'virtual'],
    visitTypes: ['prebook', 'walk-in'],
    reasonsForVisit: {
      default: [
        { label: 'Medical', value: 'Medical' },
        { label: 'Immigration Physical', value: 'Immigration Physical' },
        { label: 'Sports Physical', value: 'Sports Physical' },
        { label: 'Drug Test', value: 'Drug Test' },
        { label: 'TB Skin Test/QuantiFERON', value: 'TB Skin Test/QuantiFERON' },
      ],
    },
  },
  {
    category: {
      display: 'Occupational Medicine',
      code: 'occupational-medicine',
      system: SERVICE_CATEGORY_SYSTEM,
    },
    serviceModes: ['in-person', 'virtual'],
    visitTypes: ['prebook', 'walk-in'],
    reasonsForVisit: {
      default: [
        { label: 'Pre-employment Physical', value: 'Pre-employment Physical' },
        { label: 'Drug Test', value: 'Drug Test' },
        { label: 'TB Skin Test', value: 'TB Skin Test' },
        { label: 'Other', value: 'Other' },
      ],
    },
  },
  {
    category: { display: 'Workers Comp', code: 'workers-comp', system: SERVICE_CATEGORY_SYSTEM },
    serviceModes: ['in-person', 'virtual'],
    visitTypes: ['prebook', 'walk-in'],
    reasonsForVisit: {
      default: [{ label: 'Work Injury', value: 'Work Injury' }],
    },
  },
];

export const inPersonPrebookRoutingParams: { key: string; value: string }[] = [];

enum VisitType {
  InPersonWalkIn = 'in-person-walk-in',
  InPersonPreBook = 'in-person-pre-booked',
  InPersonPostTelemed = 'in-person-post-telemed',
  VirtualOnDemand = 'virtual-on-demand',
  VirtualScheduled = 'virtual-scheduled',
}

const BOOKING_DEFAULTS_DATA = {
  homepageOptions: [
    { id: HomepageOptions.ScheduleInPersonVisit, label: 'Schedule In-Person Visit' },
    { id: HomepageOptions.StartVirtualVisit, label: 'Start Virtual Visit' },
    { id: HomepageOptions.ScheduleVirtualVisit, label: 'Schedule Virtual Visit' },
  ] as BookingOption[],
  defaultWalkinLocationName: 'New_York',
  ehrBookingOptions: [
    {
      id: VisitType.InPersonWalkIn,
      label: 'Walk-in In Person Visit',
    },
    {
      id: VisitType.InPersonPreBook,
      label: 'Pre-booked In Person Visit',
    },
    {
      id: VisitType.VirtualOnDemand,
      label: 'On Demand Virtual Visit',
    },
    {
      id: VisitType.VirtualScheduled,
      label: 'Scheduled Virtual Visit',
    },
    {
      id: VisitType.InPersonPostTelemed,
      label: 'Post Telemed Lab Only',
    },
  ] as BookingOption[],
  serviceCategories: SERVICE_CATEGORIES_AVAILABLE,
  inPersonPrebookRoutingParams,
  // prebookMaxMonthsAhead is intentionally unset: no extended "Other dates"
  // calendar (Today/Tomorrow only). Set it (e.g. beam=12) to enable the calendar.
};

// --- End inlined data ---

const BOOKING_QUESTIONNAIRE = (): Questionnaire =>
  JSON.parse(JSON.stringify(createQuestionnaireFromConfig(getFormDefaults())));

export const selectBookingQuestionnaire: (_slot?: Slot) => {
  url: string;
  version: string;
  templateQuestionnaire: Questionnaire;
} = (_slot?: Slot): { url: string; version: string; templateQuestionnaire: Questionnaire } => {
  // can read properties of slot to determine which questionnaire to return
  // if desired. by default, we return just a single questionnaire regardless of slot
  const Q = BOOKING_QUESTIONNAIRE();
  if (!Q.url || !Q.version) {
    throw new Error('Booking questionnaire is missing url or version');
  }
  return { url: Q.url, version: Q.version, templateQuestionnaire: Q };
};

export interface BookingConfig {
  homepageOptions: BookingOption[];
  ehrBookingOptions: BookingOption[];
  serviceCategories: ServiceCategoryConfig[];
  serviceCategoryIcons?: Record<string, string>;
  formConfig: QuestionnaireConfigType;
  inPersonPrebookRoutingParams: { key: string; value: string }[];
  defaultWalkinLocationName?: string;
  // Prebook "Other dates" calendar range, in months. EHR shows the calendar and
  // uses this (default 1 when unset). Patient app shows the extended calendar
  // only when set (e.g. beam=12); unset = Today/Tomorrow only.
  prebookMaxMonthsAhead?: number;
  // Questionnaire-related fields used for building the form
  FormFields?: Record<string, unknown>;
  questionnaireBase?: QuestionnaireBase;
  hiddenFormSections?: string[];
  // Optional questionnaire canonical URLs for slot creation
  // When set, these are passed to create-slot and stored on the Slot extension
  // Used by e2e tests to inject isolated test questionnaires
  virtualQuestionnaireCanonical?: CanonicalUrl;
  inPersonQuestionnaireCanonical?: CanonicalUrl;
}

const getBookingDefaults = (): BookingConfig => ({
  ...BOOKING_DEFAULTS_DATA,
  formConfig: getFormDefaults(),
});

/**
 * Get booking configuration with optional test overrides
 *
 * @param testOverrides - Optional overrides for testing purposes
 * @returns Merged configuration
 */
export function getBookingConfig(testOverrides?: Partial<BookingConfig>): BookingConfig {
  if (!testOverrides) {
    return getBookingDefaults();
  }
  // Type assertion needed: DeepMerge with Partial<T> produces T | undefined properties,
  // but lodash merge skips undefined values so the base config properties are preserved.
  return mergeAndFreezeConfigObjects(getBookingDefaults(), testOverrides) as BookingConfig;
}

// todo: it would be nice to use zod to validate the merged booking config shape here
// Export as a getter property to allow runtime config injection in tests
export const BOOKING_CONFIG = createProxyConfigObject<BookingConfig>(getBookingConfig, CONFIG_INJECTION_KEYS.BOOKING);

// Lazy schemas — computed on each call to stay consistent with BOOKING_CONFIG,
// which may reflect runtime-injected overrides via the proxy.
// Defined as functions (not module-scope constants) to avoid eagerly accessing
// BOOKING_CONFIG during module initialization.
// Accept any URL-safe slug; downstream validates against the merged FHIR+BOOKING_CONFIG catalog.
const SERVICE_CATEGORY_CODE_SHAPE = z
  .string()
  .regex(/^[a-z0-9-]+$/i)
  .min(1)
  .max(64);
export const getServiceCategoryCodeSchema = (): z.ZodString => SERVICE_CATEGORY_CODE_SHAPE;
/** @deprecated Use getServiceCategoryCodeSchema() */
export const ServiceCategoryCodeSchema = SERVICE_CATEGORY_CODE_SHAPE;

export type ServiceCategoryCode = string;

export const getHomepageOptionSchema = (): z.ZodEnum<[string, ...string[]]> => {
  return z.enum(BOOKING_CONFIG.homepageOptions.map((opt) => opt.id) as [string, ...string[]]);
};
/** @deprecated Use getHomepageOptionSchema() — eager access can cause circular init issues */
export const HomepageOptionSchema = new Proxy({} as z.ZodEnum<[string, ...string[]]>, {
  get(_target, prop) {
    return (getHomepageOptionSchema() as any)[prop];
  },
});

export type HomepageOption = z.infer<ReturnType<typeof getHomepageOptionSchema>>;

// getReasonForVisitOptionsForServiceCategory is exported from config-helpers/booking.ts
